import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, Loader2, Eye, Megaphone, Sparkles } from "lucide-react";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { Panel, inputCls } from "@/components/admin/cc";
import { fmtDate } from "@/lib/labels";
import { SITE } from "@/constants/vhm";

const AUDIENCES = [
  { key: "", label: "Alle leads" },
  { key: "prospect", label: "Prospecten (geïmporteerd)" },
  { key: "hot", label: "Alleen HOT" },
  { key: "high", label: "Alleen HIGH" },
  { key: "normal", label: "Alleen NORMAAL" },
  { key: "low", label: "Alleen LAAG" },
];

export default function Marketing() {
  const { logout } = useAuth();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("");
  const [cta, setCta] = useState(true);
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const aiDraft = async () => {
    setAiLoading(true);
    try {
      const goal = audience === "prospect" ? "een wervingsmail naar aannemers/prospecten om samen te werken" : "";
      const { data } = await api.post("/admin/ai/marketing-draft", { goal }, { headers: authHeaders() });
      const t = (data.text || "").trim();
      const m = t.match(/^ONDERWERP:\s*(.+)/i);
      if (m) { setSubject(m[1].trim()); setBody(t.replace(/^ONDERWERP:.*\n?/i, "").trim()); }
      else { setBody(t); }
      toast.success("AI-voorstel ingevuld.");
    } catch { toast.error("AI-voorstel mislukt."); }
    finally { setAiLoading(false); }
  };
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/emails", { headers: authHeaders(), params: { limit: 60 } });
      setRecent(data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Vul een onderwerp en bericht in.");
      return;
    }
    setSending(true);
    try {
      const payload = { subject, body };
      if (audience) payload.category = audience;
      if (cta) { payload.cta_url = SITE.domain; payload.cta_label = "Vraag een offerte aan"; }
      const { data } = await api.post("/admin/campaign", payload, { headers: authHeaders() });
      if (data.recipients === 0) toast.warning("Geen ontvangers voor deze selectie.");
      else toast.success(`Verzonden naar ${data.sent} van ${data.recipients} ontvanger(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verzenden mislukt.");
    } finally {
      setSending(false);
      loadRecent();
    }
  };

  const viewEmail = (html) => {
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); }
    else toast.error("Sta pop-ups toe om de e-mail te bekijken.");
  };

  const campaigns = recent.filter((m) => m.type === "campaign");

  return (
    <AdminLayout title="Mailmarketing" subtitle="Gebrande campagne-mails naar uw leads">
      <div className="grid lg:grid-cols-2 gap-5">
        <Panel className="p-6">
          <div className="flex items-center gap-2 mb-5">
            <Megaphone className="w-5 h-5 text-cc-accent" />
            <h2 className="font-heading font-bold text-lg text-cc-ink">Nieuwe campagne</h2>
          </div>
          <label className="block font-body text-sm font-medium text-cc-sub mb-2">Doelgroep</label>
          <select data-testid="campaign-audience" value={audience} onChange={(e) => setAudience(e.target.value)} className={`${inputCls} mb-4`}>
            {AUDIENCES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>

          <label className="block font-body text-sm font-medium text-cc-sub mb-2">Onderwerp</label>
          <input data-testid="campaign-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Bv. Plan uw schilderwerken voor het najaar" className={`${inputCls} mb-4`} />

          <label className="block font-body text-sm font-medium text-cc-sub mb-2">Bericht</label>
          <textarea data-testid="campaign-body" rows={7} value={body} onChange={(e) => setBody(e.target.value)} placeholder={"Beste,\n\nBij VHM Renovation verzorgen wij uw schilder- en afwerkingswerken met oog voor detail…"} className={`${inputCls} resize-none mb-4`} />

          <label className="flex items-center gap-2 font-body text-sm text-cc-sub mb-5">
            <input type="checkbox" data-testid="campaign-cta" checked={cta} onChange={(e) => setCta(e.target.checked)} className="w-4 h-4 accent-cc-accent" />
            Knop "Vraag een offerte aan" toevoegen
          </label>

          <button onClick={aiDraft} disabled={aiLoading} type="button" data-testid="campaign-ai" className="w-full mb-3 inline-flex items-center justify-center gap-2 rounded-xl border border-cc-border bg-cc-panel2 text-cc-ink px-6 py-3 font-body font-semibold hover:border-cc-accent transition-colors disabled:opacity-60">
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-cc-accent" />} Laat AI een mail opstellen
          </button>
          <button onClick={send} disabled={sending} data-testid="campaign-send" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cc-accent text-white px-6 py-3.5 font-body font-semibold hover:bg-cc-accenth transition-colors disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Campagne versturen
          </button>
          <p className="mt-3 font-body text-xs text-cc-faint">De e-mail wordt verstuurd met een professionele VHM Renovation-template en handtekening.</p>
        </Panel>

        <Panel className="p-6">
          <h2 className="font-heading font-bold text-lg text-cc-ink mb-4">Recent verzonden</h2>
          {campaigns.length === 0 ? (
            <p className="font-body text-sm text-cc-faint">Nog geen campagnes verzonden.</p>
          ) : (
            <div className="space-y-2" data-testid="campaign-history">
              {campaigns.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl border border-cc-border bg-cc-panel2/50 px-3.5 py-3">
                  <div className="min-w-0">
                    <p className="font-body text-sm font-medium text-cc-ink truncate">{m.subject}</p>
                    <p className="font-body text-xs text-cc-faint">{m.to} · {fmtDate(m.created_at)} · <span className={m.status === "verzonden" ? "text-cc-pos" : "text-cc-hot"}>{m.status}</span></p>
                  </div>
                  <button onClick={() => viewEmail(m.html)} data-testid={`campaign-view-${m.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-cc-border px-3 py-2 font-body text-xs font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors shrink-0">
                    <Eye className="w-3.5 h-3.5" /> Bekijk
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </AdminLayout>
  );
}
